import {
	AlertCircle,
	Brain,
	Database,
	Edit,
	FileText,
	Info,
	Radio,
	Trash2,
	Zap,
} from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
	type ConnectionStatus,
	ConnectionStatusBadge,
} from "./ConnectionStatusBadge";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ConfigCardActions {
	onEdit: () => void;
	onTest: () => void;
	onDelete: () => void;
	onRag?: () => void;
}

export interface ConfigCardData {
	id: string;
	name: string;
	description?: string | null;
	generationType: string;
	isActive: boolean;
	connectionStatus: ConnectionStatus;
	connectionError?: string | null;
	providerType?: string;
	modelIdentifier?: string;
	modelDisplayName?: string | null;
	isHosted?: boolean;
	isNsfw?: boolean;
	nsfwLevel?: string | null;
	requiresAgeVerification?: boolean;
	modelCount?: number;
	hotCacheEnabled?: boolean;
	semanticCacheEnabled?: boolean;
	ragEnabled?: boolean;
}

interface ConfigCardProps {
	data: ConfigCardData;
	actions: ConfigCardActions;
	type: "route" | "model";
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function ConfigCard({ data, actions, type }: ConfigCardProps) {
	const [testing, setTesting] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const handleTest = async () => {
		setTesting(true);
		await actions.onTest();
		setTesting(false);
	};

	const handleDelete = async () => {
		if (!confirm(`Delete "${data.name}"? This action cannot be undone.`))
			return;
		setDeleting(true);
		await actions.onDelete();
		setDeleting(false);
	};

	// Get border color based on status
	const getBorderColor = () => {
		switch (data.connectionStatus) {
			case "connected":
				return "rgb(34 197 94)";
			case "error":
				return "rgb(239 68 68)";
			default:
				return "rgb(234 179 8)";
		}
	};

	return (
		<Card
			className="hover:shadow-lg transition-all border-l-4 relative"
			style={{ borderLeftColor: getBorderColor() }}
		>
			<CardHeader className="pb-4 relative">
				{/* Action Buttons */}
				<div className="absolute top-4 right-4 flex items-center gap-1 z-10">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-10 w-10 hover:bg-primary/10 hover:text-primary transition-colors"
								onClick={actions.onEdit}
								aria-label={`Edit ${type === "route" ? "configuration" : "model"}`}
							>
								<Edit className="h-4 w-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							Edit {type === "route" ? "configuration" : "model"}
						</TooltipContent>
					</Tooltip>
					{type === "route" && data.ragEnabled && actions.onRag && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-10 w-10 hover:bg-purple-500/10 hover:text-purple-600 transition-colors"
									onClick={actions.onRag}
									aria-label="Manage RAG documents"
								>
									<FileText className="h-4 w-4" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>Manage RAG Documents</TooltipContent>
						</Tooltip>
					)}
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-10 w-10 hover:bg-blue-500/10 hover:text-blue-600 transition-colors"
								onClick={handleTest}
								disabled={testing}
								aria-label="Test connection"
							>
								<Radio
									className={`h-4 w-4 ${testing ? "animate-pulse" : ""}`}
								/>
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							{testing
								? "Testing connection..."
								: type === "route"
									? "Ping connection to verify status"
									: "Test connection"}
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-10 w-10 hover:bg-destructive/10 hover:text-destructive transition-colors"
								onClick={handleDelete}
								disabled={deleting}
								aria-label={`Delete ${type === "route" ? "configuration" : "model"}`}
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							Delete this {type === "route" ? "configuration" : "model"}
						</TooltipContent>
					</Tooltip>
				</div>

				{/* Title and Primary Info */}
				<div className="space-y-3 pr-52 min-w-0">
					<div className="space-y-1.5 min-w-0">
						<CardTitle
							className="text-xl font-bold break-words"
							style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
						>
							{data.name}
						</CardTitle>
						{data.description && (
							<CardDescription className="text-sm line-clamp-2">
								{data.description}
							</CardDescription>
						)}
					</div>

					{/* Status and Metadata Badges */}
					<div className="flex items-center gap-2 flex-wrap">
						<ConnectionStatusBadge
							status={data.connectionStatus}
							errorMessage={data.connectionError}
						/>
						<Badge
							variant={data.isActive ? "default" : "secondary"}
							className="text-xs"
						>
							{data.isActive ? "Active" : "Inactive"}
						</Badge>
						<Badge variant="outline" className="text-xs capitalize">
							{data.generationType}
						</Badge>
						{data.isNsfw && (
							<Tooltip>
								<TooltipTrigger asChild>
									<Badge
										variant="outline"
										className="text-xs border-orange-500 text-orange-400 bg-orange-950"
									>
										<AlertCircle className="h-3 w-3 mr-1" />
										NSFW
									</Badge>
								</TooltipTrigger>
								<TooltipContent>
									<p>
										This {type === "route" ? "route" : "model"} contains NSFW
										content
										{data.nsfwLevel && (
											<span className="block text-xs mt-1 capitalize">
												Level: {data.nsfwLevel}
											</span>
										)}
										{data.requiresAgeVerification && (
											<span className="block text-xs mt-1">
												Age verification recommended
											</span>
										)}
									</p>
								</TooltipContent>
							</Tooltip>
						)}
						{type === "route" && data.modelCount && data.modelCount > 1 && (
							<Badge variant="outline" className="text-xs">
								{data.modelCount} models
							</Badge>
						)}
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-0">
				<div className="space-y-4">
					{/* Model/Provider Info */}
					{data.providerType || data.modelIdentifier ? (
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<div className="flex items-center gap-1.5">
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
										Provider
									</p>
									<Tooltip>
										<TooltipTrigger asChild>
											<Info className="h-3 w-3 text-muted-foreground cursor-help opacity-60 hover:opacity-100 transition-opacity" />
										</TooltipTrigger>
										<TooltipContent>
											<p>The AI provider service (e.g., Ollama, OpenAI)</p>
										</TooltipContent>
									</Tooltip>
								</div>
								<p className="text-base font-semibold capitalize text-foreground">
									{data.providerType || "N/A"}
								</p>
							</div>
							<div className="space-y-2">
								<div className="flex items-center gap-1.5">
									<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
										Model
									</p>
									<Tooltip>
										<TooltipTrigger asChild>
											<Info className="h-3 w-3 text-muted-foreground cursor-help opacity-60 hover:opacity-100 transition-opacity" />
										</TooltipTrigger>
										<TooltipContent>
											<p>The specific AI model identifier</p>
										</TooltipContent>
									</Tooltip>
								</div>
								<p className="text-base font-mono font-semibold truncate text-foreground">
									{data.modelDisplayName || data.modelIdentifier || "N/A"}
								</p>
							</div>
						</div>
					) : (
						<div className="p-3 bg-muted/30 rounded-lg border border-dashed">
							<p className="text-sm text-muted-foreground text-center">
								No models configured
							</p>
						</div>
					)}

					{/* Mode (for model configs or route configs with model info) */}
					{data.isHosted !== undefined && (
						<div className="space-y-2">
							<div className="flex items-center gap-1.5">
								<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
									Mode
								</p>
								<Tooltip>
									<TooltipTrigger asChild>
										<Info className="h-3 w-3 text-muted-foreground cursor-help opacity-60 hover:opacity-100 transition-opacity" />
									</TooltipTrigger>
									<TooltipContent>
										<p>
											{data.isHosted
												? "Hosted: Nova manages the infrastructure"
												: "BYOP: Bring Your Own Provider - you provide credentials"}
										</p>
									</TooltipContent>
								</Tooltip>
							</div>
							<Badge
								variant={data.isHosted ? "default" : "secondary"}
								className="text-sm px-2.5 py-1"
							>
								{data.isHosted ? "Hosted" : "BYOP"}
							</Badge>
						</div>
					)}

					{/* Features (for route configs) */}
					{type === "route" &&
						(data.hotCacheEnabled ||
							data.semanticCacheEnabled ||
							data.ragEnabled) && (
							<div className="pt-3 border-t">
								<div className="flex flex-wrap gap-2">
									{data.hotCacheEnabled && (
										<Tooltip>
											<TooltipTrigger asChild>
												<Badge
													variant="outline"
													className="text-xs gap-1.5 cursor-help px-2.5 py-1 hover:bg-accent transition-colors"
												>
													<Zap className="h-3.5 w-3.5" />
													Hot Cache
												</Badge>
											</TooltipTrigger>
											<TooltipContent>
												<p>Exact-match caching for instant responses</p>
											</TooltipContent>
										</Tooltip>
									)}
									{data.semanticCacheEnabled && (
										<Tooltip>
											<TooltipTrigger asChild>
												<Badge
													variant="outline"
													className="text-xs gap-1.5 cursor-help px-2.5 py-1 hover:bg-accent transition-colors"
												>
													<Brain className="h-3.5 w-3.5" />
													Semantic Cache
												</Badge>
											</TooltipTrigger>
											<TooltipContent>
												<p>Similarity-based caching for related queries</p>
											</TooltipContent>
										</Tooltip>
									)}
									{data.ragEnabled && (
										<Tooltip>
											<TooltipTrigger asChild>
												<Badge
													variant="outline"
													className="text-xs gap-1.5 cursor-help px-2.5 py-1 hover:bg-accent transition-colors"
												>
													<Database className="h-3.5 w-3.5" />
													RAG
												</Badge>
											</TooltipTrigger>
											<TooltipContent>
												<p>Retrieval Augmented Generation enabled</p>
											</TooltipContent>
										</Tooltip>
									)}
								</div>
							</div>
						)}

					{/* Connection Error */}
					{data.connectionError && (
						<Alert variant="destructive" className="mt-2">
							<AlertCircle className="h-4 w-4" />
							<AlertDescription className="text-xs">
								<span className="font-medium">Connection Error:</span>{" "}
								{data.connectionError}
							</AlertDescription>
						</Alert>
					)}
				</div>
			</CardContent>
		</Card>
	);
}
